import { useEffect, useState, useMemo } from "react";
import { useLocation } from "wouter";
import { useUser } from "@/lib/user";
import { api } from "@/lib/api";
import { QUEST_META, PARTS_ORDER } from "@/lib/quests";
import { QuestNode } from "@/components/QuestNode";
import { Button } from "@/components/ui/button";
import { Trophy, LogOut, BookOpen, Users } from "lucide-react";

const BOOK_PDF_URL = "/founder-mode-book.pdf";
const COMMUNITY_URL =
  "https://chat.whatsapp.com/C7U7EsUvG5W5RmxaNLDXkJ?s=cl&p=a&ilr=4";

export default function DashboardPage() {
  const [, navigate] = useLocation();
  const { user, clearUser } = useUser();
  const [progress, setProgress] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [myRank, setMyRank] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    const controller = new AbortController();
    let alive = true;

    (async () => {
      try {
        const list = await api.getProgressAll(user.id, { signal: controller.signal });
        if (alive) setProgress(list);
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (alive) setLoadError("Could not load progress.");
      }
    })();

    // Fetch my rank for the current month (non-blocking, swallow abort/network errors)
    (async () => {
      try {
        const r = await api.getUserRank(user.id, undefined, { signal: controller.signal });
        if (alive && r && r.rank != null) setMyRank(r);
      } catch (_) {
        // non-blocking
      }
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, [user, navigate]);

  const byQuest = useMemo(() => {
    const m = {};
    (progress || []).forEach((p) => (m[p.quest_id] = p));
    return m;
  }, [progress]);

  const stats = useMemo(() => {
    if (!progress) return { xp: 0, completed: 0, avg: null };
    let xp = 0;
    let completed = 0;
    let scoreSum = 0;
    let scoreCount = 0;
    progress.forEach((p) => {
      if (p.status === "completed") {
        const meta = QUEST_META.find((q) => q.id === p.quest_id);
        if (meta) xp += meta.xp;
        completed += 1;
        if (typeof p.score === "number") {
          scoreSum += p.score;
          scoreCount += 1;
        }
      }
    });
    return {
      xp,
      completed,
      avg: scoreCount ? scoreSum / scoreCount : null,
    };
  }, [progress]);

  function handleQuestClick(quest, status) {
    if (status === "locked") {
      if (quest.id >= 2 && !user?.is_paid) {
        navigate("/paywall");
      }
      return;
    }
    if (status === "completed") {
      navigate(`/scorecard/${quest.id}`);
      return;
    }
    navigate(`/quest/${quest.id}`);
  }

  if (!user) return null;

  return (
    <main data-testid="dashboard-page" className="min-h-screen px-6 sm:px-10 lg:px-20 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Top bar */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-300">
              welcome back
            </p>
            <h1 data-testid="dashboard-greeting" className="mt-2 text-3xl md:text-4xl font-semibold text-zinc-100 tracking-tight">
              {user.name || "Founder"}<span className="text-amber-400">.</span>
            </h1>
            {user.founder_persona && (
              <span
                data-testid="persona-badge"
                className="mt-3 inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/[0.06] px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-amber-300"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                {user.founder_persona}
              </span>
            )}
            {user.is_paid && (
              <div
                data-testid="founder-pass-widget"
                className="mt-4 inline-flex flex-wrap items-center gap-2"
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                  founder pass
                </span>
                <a
                  data-testid="fp-book-link"
                  href={BOOK_PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/[0.06] hover:bg-amber-500/[0.12] px-2.5 py-1 text-[12px] text-amber-200 transition-colors"
                >
                  <BookOpen className="h-3.5 w-3.5" /> Book PDF
                </a>
                <a
                  data-testid="fp-community-link"
                  href={COMMUNITY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/[0.06] hover:bg-amber-500/[0.12] px-2.5 py-1 text-[12px] text-amber-200 transition-colors"
                >
                  <Users className="h-3.5 w-3.5" /> Community
                </a>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="leaderboard-link"
              onClick={() => navigate("/leaderboard")}
              variant="outline"
              className="border-amber-500/30 bg-transparent text-amber-300 hover:bg-amber-500/10 hover:text-amber-200 gap-2"
            >
              <Trophy className="h-4 w-4" /> Leaderboard
            </Button>
            <Button
              data-testid="dashboard-signout"
              onClick={() => {
                clearUser();
                navigate("/");
              }}
              variant="ghost"
              className="text-zinc-500 hover:text-zinc-300 gap-2"
            >
              <LogOut className="h-4 w-4" /> Exit
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div data-testid="dashboard-stats" className="mt-10 grid grid-cols-3 gap-4 max-w-2xl">
          <Stat label="total xp" value={stats.xp} mono />
          <Stat label="quests done" value={`${stats.completed}/13`} mono />
          <Stat
            label="avg score"
            value={stats.avg != null ? Math.round(stats.avg) : 0}
            mono
          />
        </div>

        {myRank && myRank.rank && (
          <button
            data-testid="dashboard-rank-chip"
            onClick={() => navigate("/leaderboard")}
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-amber-400/50 bg-amber-500/[0.08] px-4 py-1.5 text-sm text-amber-200 hover:bg-amber-500/[0.14] transition-colors"
          >
            <Trophy className="h-3.5 w-3.5" />
            Your rank this month: <span className="font-mono font-semibold">#{myRank.rank}</span>
            <span className="text-zinc-500">→ Leaderboard</span>
          </button>
        )}

        {loadError && (
          <p className="mt-8 text-sm text-red-400" data-testid="dashboard-load-error">{loadError}</p>
        )}

        {/* Quest map */}
        <div className="mt-14 space-y-12">
          {PARTS_ORDER.map((partName) => {
            const quests = QUEST_META.filter((q) => q.part === partName);
            if (quests.length === 0) return null;
            return (
              <section key={partName} data-testid={`part-${partName.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                <header className="mb-5">
                  <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-amber-300">
                    {partName.split("·")[0]}
                  </p>
                  <h2 className="text-xl font-semibold text-zinc-100">
                    {partName.includes("·") ? partName.split("·")[1].trim() : partName}
                  </h2>
                </header>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {quests.map((q) => (
                    <QuestNode
                      key={q.id}
                      quest={q}
                      progress={byQuest[q.id]}
                      onClick={handleQuestClick}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </main>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="rounded-xl border border-zinc-800/80 bg-zinc-950/40 p-5">
      <div className={`text-2xl ${mono ? "font-mono" : ""} text-zinc-100 leading-none`}>{value}</div>
      <div className="mt-2 text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
    </div>
  );
}
